/* eslint-disable @next/next/no-img-element */
'use client'

import { createThumbnailHoverHandlers } from '@/components/video/HoverAnimatedThumbnail'
import { trackSessionStart } from '@/lib/analytics'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { FC, useEffect, useRef, useState } from 'react'
import {
  aspectRatioToPaddingBottom,
  DEFAULT_TILE_ASPECT_RATIO,
  type TileAspectRatio,
} from '@/components/scenario/aspectRatio'
import {
  DEFAULT_THUMBNAIL_ROUNDED,
  THUMBNAIL_RADIUS_PC,
  THUMBNAIL_RADIUS_SP,
  thumbnailRadius,
} from '@/components/scenario/thumbnailCorner'
import { DEFAULT_EMBED_FONT, type EmbedFont } from '@/components/scenario/embedFont'
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch'
import { useViewportVideoPrefetch } from '@/hooks/useViewportVideoPrefetch'
import { useBridge } from './bridge/BridgeContext'
import { VideoModal } from './modal'
import { useShadowHostPortal } from './shadow-host-portal/ShadowHostPortalContext'
import UnifiedPlayMark from './UnifiedPlayMark'

export interface TilePlayerProps {
  nodes: ScenarioNodeWithVideoUrl[]
  videoOrder: string[]
  columns: number
  mobileColumns?: number
  aspectRatio?: TileAspectRatio
  /** サムネイルの角丸。false なら直角（先方サイトのデザインに合わせる設定） */
  thumbnailRounded?: boolean
  /** 視聴モーダルのフォント（プレイリスト単位の設定） */
  embedFont?: EmbedFont
  scenarioId?: string
  /** プレイリスト共通CTA（動画CTA未設定時の既定値・ADR-0019） */
  ctaUrl?: string | null
  ctaLabel?: string | null
}

const TilePlayer: FC<TilePlayerProps> = ({
  nodes,
  videoOrder,
  columns,
  mobileColumns = 2,
  aspectRatio = DEFAULT_TILE_ASPECT_RATIO,
  thumbnailRounded = DEFAULT_THUMBNAIL_ROUNDED,
  embedFont = DEFAULT_EMBED_FONT,
  scenarioId,
  ctaUrl,
  ctaLabel,
}) => {
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number | null>(null)
  const [isPiPActive, setIsPiPActive] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTrackedRef = useRef(false)
  const { schedulePrefetch, cancelPrefetch } = useHoverPrefetch()

  // Track SESSION_START when tile player becomes visible
  useEffect(() => {
    if (!scenarioId || hasTrackedRef.current) return
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true
          trackSessionStart(scenarioId)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [scenarioId])

  const orderedNodes =
    videoOrder && videoOrder.length > 0
      ? videoOrder
          .map((videoId) => nodes.find((node) => node.videoId === videoId))
          .filter((node): node is ScenarioNodeWithVideoUrl => node !== undefined)
      : nodes
  useViewportVideoPrefetch(containerRef, orderedNodes[0]?.videoUrl)

  const bridge = useBridge()
  const handleTileClick = (index: number) => {
    if (bridge) {
      bridge.sendOpenIntent(orderedNodes[index]?.videoId)
      return
    }
    setSelectedVideoIndex(index)
  }

  // モーダル展開中は shadowHost を body 直下へ portal して、先方サイトの
  // stacking context（z-index 競合や祖先の position: relative）を回避する。
  const portal = useShadowHostPortal()
  const isOverlayActive = selectedVideoIndex !== null || isPiPActive
  useEffect(() => {
    portal?.setPortalActive(isOverlayActive)
  }, [isOverlayActive, portal])
  // アンマウント時は確実に portal 状態を戻す（DOM が body 末尾に取り残されないように）
  useEffect(() => {
    return () => {
      portal?.setPortalActive(false)
    }
  }, [portal])

  const handleCloseModal = () => {
    setSelectedVideoIndex(null)
  }

  const handleRequestOpen = (videoIndex: number) => {
    setSelectedVideoIndex(videoIndex)
  }

  return (
    <>
      <style>{`
        .tile-grid {
          grid-template-columns: repeat(${mobileColumns}, minmax(0, 1fr));
        }
        @media (min-width: 768px) {
          .tile-grid {
            grid-template-columns: repeat(${columns}, minmax(0, 1fr));
          }
        }
        .tile-card {
          border-radius: ${thumbnailRadius(thumbnailRounded, THUMBNAIL_RADIUS_SP)};
        }
        @media (min-width: 768px) {
          .tile-card {
            border-radius: ${thumbnailRadius(thumbnailRounded, THUMBNAIL_RADIUS_PC)};
          }
        }
        /* 再生アイコン常時表示（控えめ）+ hover時に暗転 */
        .tile-card .play-overlay {
          opacity: 1 !important;
          background: transparent;
        }
        .tile-card:hover .play-overlay {
          background: rgba(0, 0, 0, 0.3);
        }
        /* hover時のみ表示に戻す場合はこちらに切り替え：
        .tile-card .play-overlay {
          opacity: 0;
          background: rgba(0, 0, 0, 0.3);
        }
        .tile-card:hover .play-overlay {
          opacity: 1 !important;
        }
        */
      `}</style>

      {/*
        モーダル展開中はタイル群を visibility: hidden で見えなくする。
        PR #95 で shadowHost を body 直下へ portal するようになった結果、
        shadowHost 内の React tree（タイル + モーダル）が一緒に最前面に出るため、
        backdrop の半透明（rgba(0,0,0,0.75)）越しにタイル群が透けて見えてしまう問題への対応。
        - 期待背景: ユーザーが直前まで見ていた先方サイトのページ
        - 直前までの: 中間レイヤーに残っていたタイル群

        visibility: hidden を選ぶ理由（display: none ではなく）:
        - レイアウトを保つことで、iframe モード（STUDIO）の auto-height 調整に影響しない
          （PR #96 の「iframe が 24px に縮む」副作用の再発を防ぐ）
        - direct モードでは shadowHost 自体が fixed-fullscreen のため、
          残った layout 領域は最前面の backdrop / モーダルに完全に被われる
      */}
      <div
        ref={containerRef}
        className={`w-full max-w-screen-xl mx-auto p-0 pb-1 ${
          isOverlayActive ? 'invisible' : ''
        }`}
      >
        <div className="tile-grid grid gap-5">
          {orderedNodes.map((node, index) => (
            <div
              key={node.id}
              className="tile-card group relative cursor-pointer overflow-hidden bg-gray-100 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
              onClick={() => handleTileClick(index)}
              onPointerEnter={() => schedulePrefetch(node.videoUrl)}
              onPointerLeave={cancelPrefetch}
            >
              <div className="relative w-full" style={{ paddingBottom: aspectRatioToPaddingBottom(aspectRatio) }}>
                {node.thumbnailUrl ? (
                  <>
                    <img
                      src={node.thumbnailUrl}
                      alt={`動画 ${index + 1}`}
                      className="absolute top-0 left-0 w-full h-full object-cover"
                      {...createThumbnailHoverHandlers(node.thumbnailUrl, node.animatedThumbnailUrl)}
                    />
                    <div className="play-overlay pointer-events-none absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-0 transition-all duration-200">
                      <UnifiedPlayMark />
                    </div>
                  </>
                ) : (
                    <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center bg-gray-500">
                      <UnifiedPlayMark />
                      <p className="mt-2 text-[14px] text-gray-600">動画 {index + 1}</p>
                    </div>
                )}
              </div>
              {/* <div className="px-3 py-3">
                <div className="text-[14px] font-bold text-gray-800 line-clamp-1">
                  {node.video?.title || node.title || `動画 ${index + 1}`}
                </div>
                {node.video?.description && (
                  <div className="text-[12px] text-gray-500 mt-1 line-clamp-2">{node.video.description}</div>
                )}
              </div> */}
            </div>
          ))}
        </div>
      </div>

      <VideoModal
        nodes={nodes}
        videoOrder={videoOrder}
        initialVideoIndex={selectedVideoIndex ?? 0}
        isOpen={selectedVideoIndex !== null}
        onClose={handleCloseModal}
        onRequestOpen={handleRequestOpen}
        onPiPActiveChange={setIsPiPActive}
        scenarioId={scenarioId}
        ctaUrl={ctaUrl}
        ctaLabel={ctaLabel}
        embedFont={embedFont}
      />
    </>
  )
}

export default TilePlayer
